import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { useGeneratePage, type ProjectRow, type ContentPlan } from '../../hooks/useProjects.js';

interface Props {
  project: ProjectRow;
}

function parsePlan(raw: string | null | undefined): ContentPlan | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as ContentPlan; } catch { return null; }
}

export function GeneratePagePrompt({ project }: Props) {
  const { t } = useTranslation();
  const generatePage = useGeneratePage(project.id);

  const plan = parsePlan(project.contentPlan);
  const hasBlocks = (plan?.blocks.length ?? 0) > 0;

  function handleGenerate() {
    generatePage.mutate();
  }

  return (
    <div style={{ maxWidth: '720px' }}>

      {/* Template card */}
      <div style={{
        background: 'var(--evo-bg-2)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r)',
        padding: 'var(--evo-space-5)',
        marginBottom: 'var(--evo-space-5)',
      }}>
        <div style={{
          fontSize: 'var(--evo-fs-label)',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
          marginBottom: 'var(--evo-space-1)',
        }}>
          {t('projects.generate.templateLabel')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)' }}>
          <span style={{
            fontSize: 'var(--evo-fs-h3)',
            fontWeight: 'var(--evo-fw-semibold)',
            color: 'var(--evo-fg-1)',
            fontFamily: 'var(--evo-font)',
          }}>
            {project.templateName ?? project.templateSlug}
          </span>
          <a
            href={`/templates/${project.templateSlug}/showcase.html`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 'var(--evo-fs-label)',
              color: 'var(--evo-cyan)',
              fontFamily: 'var(--evo-font)',
              textDecoration: 'none',
            }}
          >
            {t('projects.gallery.showcase')}
          </a>
        </div>

        {plan && (
          <div style={{
            marginTop: 'var(--evo-space-3)',
            fontSize: 'var(--evo-fs-body)',
            color: 'var(--evo-fg-3)',
            fontFamily: 'var(--evo-font)',
          }}>
            {t('projects.generate.planBlocks', { n: plan.blocks.length })}
          </div>
        )}
      </div>

      {/* No content plan warning */}
      {!hasBlocks && (
        <div style={{
          padding: 'var(--evo-space-3) var(--evo-space-4)',
          background: 'var(--evo-warn-bg, rgba(234,179,8,0.08))',
          border: '1px solid var(--evo-warn, rgba(234,179,8,0.3))',
          borderRadius: 'var(--evo-r-sm)',
          fontSize: 'var(--evo-fs-body)',
          color: 'var(--evo-fg-2)',
          fontFamily: 'var(--evo-font)',
          marginBottom: 'var(--evo-space-4)',
        }}>
          {t('projects.generate.noContentPlan')}
        </div>
      )}

      {/* Error state */}
      {generatePage.isError && (
        <div style={{
          padding: 'var(--evo-space-3) var(--evo-space-4)',
          background: 'rgba(239,68,68,0.06)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--evo-r-sm)',
          fontSize: 'var(--evo-fs-body)',
          color: 'var(--evo-fg-2)',
          fontFamily: 'var(--evo-font)',
          marginBottom: 'var(--evo-space-4)',
        }}>
          {t('projects.generate.error')}{': '}
          {generatePage.error instanceof Error ? generatePage.error.message : t('projects.generate.errorGeneric')}
        </div>
      )}

      {/* CTA */}
      {generatePage.isPending ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-space-3)',
          padding: 'var(--evo-space-3) 0',
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            border: '2px solid var(--evo-stroke)',
            borderTopColor: 'var(--evo-cyan)',
            animation: 'evo-spin 0.7s linear infinite',
            flexShrink: 0,
          }} />
          <span style={{
            fontSize: 'var(--evo-fs-body)',
            color: 'var(--evo-fg-3)',
            fontFamily: 'var(--evo-font)',
          }}>
            {t('projects.generate.generating')}
          </span>
        </div>
      ) : (
        <Button
          size="md"
          variant="solid"
          tone="cyan"
          onClick={handleGenerate}
          disabled={!hasBlocks}
        >
          {generatePage.isError ? t('projects.generate.retry') : t('projects.generate.btn')}
        </Button>
      )}
    </div>
  );
}
